import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Typography, Stack, Switch, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { FiRepeat, FiCalendar, FiSave, FiRefreshCw, FiLock } from "react-icons/fi";
import moment from "moment";

const UI = {
  bg: "#FFFFFF",
  surface: "#F8FAFC",
  border: "#CBD5E1",
  borderLight: "#E2E8F0",
  text: "#0F172A",
  textSub: "#334155",
  textMuted: "#64748B",
  accent: "#0D9488",
  accentDark: "#0F766E",
  accentSoft: "#CCFBF1",
  accentBorder: "#99F6E4",
  accentLight: "#F0FDFA",
  accentGlow: "rgba(13,148,136,0.14)",
};

const toInputVal = (dateVal) => {
  if (!dateVal) return "";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "";
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return "";
  }
};

const fmtDisplay = (dateVal) => {
  if (!dateVal) return "Not set";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "Not set";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "Not set";
  }
};

// Compute which 1sts-of-month fall within [start, end]
const getCronFires = (startStr, endStr) => {
  if (!startStr || !endStr) return [];
  const start = moment(startStr);
  const end = moment(endStr);
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return [];
  const fires = [];
  let cur = start.clone().startOf("month");
  while (cur.isSameOrBefore(end, "month")) {
    const first = cur.clone().date(1);
    if (first.isSameOrAfter(start, "day") && first.isSameOrBefore(end, "day")) {
      fires.push(first.clone());
    }
    cur.add(1, "month");
  }
  return fires;
};

// ── Date input box ──────────────────────────────────────────────────────────────
const DateBox = ({ label, value, onChange, isDirty }) => (
  <Box sx={{ flex: 1 }}>
    <Typography sx={{ fontSize: "10px", fontWeight: 600, color: UI.textMuted, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.07em" }}>
      {label}
    </Typography>
    <Box
      sx={{
        px: "10px", py: "7px", borderRadius: "8px",
        border: `1.5px solid ${isDirty ? UI.accent : UI.borderLight}`,
        background: isDirty ? UI.accentLight : UI.surface,
        "&:hover": { border: `1.5px solid ${UI.accent}`, background: UI.accentLight },
        transition: "all 0.15s",
      }}
    >
      <Box
        component="input"
        type="date"
        value={value}
        onChange={onChange}
        sx={{
          width: "100%", border: "none", background: "transparent",
          outline: "none", fontSize: "12.5px", fontWeight: 600,
          color: value ? UI.text : UI.textMuted,
          fontFamily: "inherit", cursor: "pointer",
        }}
      />
    </Box>
  </Box>
);

// ── Cron preview (same logic as AddTodoTaskModal) ──────────────────────────────
const CronPreview = ({ startStr, endStr }) => {
  if (!startStr || !endStr) return null;
  const start = moment(startStr);
  const end = moment(endStr);
  if (!start.isValid() || !end.isValid()) return null;
  if (end.isBefore(start)) {
    return (
      <Box sx={{ mt: 1.5, p: 1.4, borderRadius: "10px", background: "#FEF2F2", border: "1.5px solid #FECACA" }}>
        <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "#DC2626" }}>
          End date must be after start date.
        </Typography>
      </Box>
    );
  }

  const fires = getCronFires(startStr, endStr);
  const totalDays = end.diff(start, "day") + 1;
  const valid = fires.length > 0;

  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
      <Box sx={{ mt: 1.5, borderRadius: "10px", overflow: "hidden", border: `1.5px solid ${valid ? `${UI.accent}30` : "#FECACA"}` }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.6, py: 1.1, bgcolor: valid ? `${UI.accent}08` : "#FEF2F2", borderBottom: `1px solid ${valid ? `${UI.accent}20` : "#FECACA"}` }}>
          <FiRefreshCw size={12} color={valid ? UI.accent : "#DC2626"} />
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: valid ? UI.accentDark : "#DC2626" }}>
            {start.format("DD MMM YYYY")}
            <Box component="span" sx={{ mx: 0.6, color: valid ? UI.textMuted : "#FCA5A5" }}>→</Box>
            {end.format("DD MMM YYYY")}
            <Box component="span" sx={{ mx: 0.8, color: valid ? UI.textMuted : "#FCA5A5", fontWeight: 400 }}>·</Box>
            {totalDays} day{totalDays !== 1 ? "s" : ""}
          </Typography>
        </Stack>

        {valid ? (
          <Box sx={{ px: 1.6, py: 1.2, bgcolor: "#FAFAFA" }}>
            <Stack direction="row" alignItems="center" spacing={0.8} mb={1}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#22C55E" }} />
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#15803D" }}>
                Task will auto-create {fires.length} time{fires.length !== 1 ? "s" : ""} — on the 1st of each month below
              </Typography>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={0.8}>
              {fires.map((d, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.1, py: 0.4, borderRadius: "6px", bgcolor: UI.accentLight, border: `1px solid ${UI.accent}35` }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: UI.accent, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: UI.accentDark }}>
                      1 {d.format("MMM YYYY")}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Stack>
          </Box>
        ) : (
          <Box sx={{ px: 1.6, py: 1.2, bgcolor: "#FEF2F2" }}>
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Box sx={{ fontSize: "14px", lineHeight: 1.3, mt: "1px", flexShrink: 0 }}>⚠️</Box>
              <Box>
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#DC2626", mb: 0.3 }}>
                  Task won't repeat — date range has no valid monthly start
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "#EF4444", lineHeight: 1.5 }}>
                  This task repeats on the <b>1st of every month</b>. Your range ({start.format("DD MMM")} – {end.format("DD MMM YYYY")}) doesn't include any month's 1st, so it will never auto-create. Extend the range to cover at least one month's 1st.
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </Box>
    </motion.div>
  );
};

// Inject recurSpin keyframe once
if (typeof document !== "undefined" && !document.getElementById("recur-spin-style")) {
  const s = document.createElement("style");
  s.id = "recur-spin-style";
  s.textContent = "@keyframes recurSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }";
  document.head.appendChild(s);
}

// ── Main component ─────────────────────────────────────────────────────────────
const RecurringCard = ({ selectedData, isAdmin, onUpdate, onOpenTemplate, onCreateTemplate, accessDenied, onClearAccessDenied }) => {
  const hasTemplate     = Boolean(selectedData?.recurringTemplateId);
  const headerClickable = true;
  const headerClick     = hasTemplate ? onOpenTemplate : onCreateTemplate;
  const savedIsRecurring = Boolean(selectedData?.isRecurring);

  // localEnabled tracks the toggle UI state — not saved until Save is clicked
  const [localEnabled, setLocalEnabled] = useState(savedIsRecurring);
  const [draft, setDraft] = useState({
    startDate: toInputVal(selectedData?.recurringStartDate),
    endDate: toInputVal(selectedData?.recurringEndDate),
  });
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Sync when selectedData refreshes after a save
  useEffect(() => {
    setLocalEnabled(Boolean(selectedData?.isRecurring));
    setDraft({
      startDate: toInputVal(selectedData?.recurringStartDate),
      endDate: toInputVal(selectedData?.recurringEndDate),
    });
  }, [selectedData?.isRecurring, selectedData?.recurringStartDate, selectedData?.recurringEndDate]);

  const savedStart = toInputVal(selectedData?.recurringStartDate);
  const savedEnd = toInputVal(selectedData?.recurringEndDate);
  const isDirty = draft.startDate !== savedStart || draft.endDate !== savedEnd || localEnabled !== savedIsRecurring;

  // Cron must have at least one valid fire AND end must not be before start
  const isDateRangeValid =
    draft.startDate &&
    draft.endDate &&
    !moment(draft.endDate).isBefore(moment(draft.startDate)) &&
    getCronFires(draft.startDate, draft.endDate).length > 0;

  const canSave = localEnabled && isDateRangeValid && isDirty;

  const handleToggle = () => {
    if (localEnabled) {
      // Turning OFF → confirm first
      setConfirmOpen(true);
    } else {
      // Turning ON → just enable locally, no API call
      setLocalEnabled(true);
    }
  };

  const handleConfirmDisable = async () => {
    setConfirmOpen(false);
    setLocalEnabled(false);
    setDraft({ startDate: "", endDate: "" });
    await onUpdate({ isRecurring: false });
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    await onUpdate({
      isRecurring: true,
      recurringStartDate: draft.startDate,
      recurringEndDate: draft.endDate,
    });
    setSaving(false);
  };

  if (accessDenied) {
    return (
      <Box
        sx={{
          background: UI.bg,
          border: "1.5px solid #FECACA",
          borderRadius: "14px",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(15,23,42,0.06)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.8 }}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: "10px", bgcolor: "#FEF2F2",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            <FiLock size={16} color="#DC2626" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#DC2626", fontFamily: "'Inter', sans-serif" }}>
              Access Restricted
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "#EF4444", fontFamily: "'Inter', sans-serif", mt: 0.2 }}>
              You are not the creator of this template
            </Typography>
          </Box>
          <Typography
            onClick={onClearAccessDenied}
            sx={{ fontSize: "11px", color: "#94A3B8", cursor: "pointer", flexShrink: 0, "&:hover": { color: "#475569" } }}
          >
            Dismiss
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: UI.bg,
        border: `1.5px solid ${localEnabled ? UI.accentBorder : UI.borderLight}`,
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: localEnabled ? `0 0 0 3px ${UI.accentGlow}` : "0 2px 12px rgba(15,23,42,0.06)",
        transition: "all 0.22s ease",
      }}
    >
      {/* ── Header ── */}
      <Box
        onClick={headerClickable ? headerClick : undefined}
        sx={{
          px: 2, py: 1.5,
          background: localEnabled
            ? `linear-gradient(135deg, ${UI.accentLight} 0%, #fff 100%)`
            : "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
          borderBottom: localEnabled ? `1.5px solid ${UI.accentBorder}` : "none",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
          transition: "all 0.22s ease",
          cursor: headerClickable ? "pointer" : "default",
          ...(headerClickable && {
            "&:hover": { background: `linear-gradient(135deg, ${UI.accentLight} 0%, #E0FEF9 100%)` },
          }),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: "10px",
              bgcolor: localEnabled ? UI.accent : "#F1F5F9",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: localEnabled ? "0 4px 12px rgba(13,148,136,0.3)" : "none",
              transition: "all 0.22s ease",
              flexShrink: 0,
            }}
          >
            <FiRefreshCw
              size={16}
              color={localEnabled ? "#fff" : UI.textMuted}
              style={{
                transition: "color 0.22s",
                animation: localEnabled ? "recurSpin 1.8s linear infinite" : "none",
              }}
            />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "13px", color: UI.text, lineHeight: 1.2, fontFamily: "'Inter', sans-serif" }}>
              Recurring Task
            </Typography>
            <Typography sx={{ fontSize: "11px", color: UI.accent, fontWeight: 500, fontFamily: "'Inter', sans-serif" }}>
              {hasTemplate ? "Click to view & edit template" : "Click to create template"}
            </Typography>
          </Box>
        </Stack>

      </Box>

      {/* ── Body: date inputs + cron preview + save ── */}
      <AnimatePresence>
        {localEnabled && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: "hidden" }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              {/* Period label */}
              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1.2, pt: 0.5, borderTop: `1px dashed ${UI.accent}40` }}>
                <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: UI.accent }} />
                <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: UI.accentDark, textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "'Inter', sans-serif" }}>
                  Recurrence Period
                </Typography>
              </Stack>

              {/* Date row */}
              {isAdmin ? (
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <DateBox
                    label="Start"
                    value={draft.startDate}
                    onChange={(e) => setDraft((d) => ({ ...d, startDate: e.target.value }))}
                    isDirty={draft.startDate !== savedStart}
                  />
                  <Typography sx={{ color: UI.textMuted, fontSize: "16px", mt: "24px", flexShrink: 0 }}>→</Typography>
                  <DateBox
                    label="End"
                    value={draft.endDate}
                    onChange={(e) => setDraft((d) => ({ ...d, endDate: e.target.value }))}
                    isDirty={draft.endDate !== savedEnd}
                  />
                </Stack>
              ) : (
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 600, color: UI.textMuted, mb: 0.4, textTransform: "uppercase" }}>Start</Typography>
                    <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: savedStart ? UI.text : UI.textMuted, fontStyle: savedStart ? "normal" : "italic", fontFamily: "'Inter', sans-serif" }}>
                      {fmtDisplay(selectedData?.recurringStartDate)}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: UI.textMuted, fontSize: "16px", mt: 1.5, flexShrink: 0 }}>→</Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 600, color: UI.textMuted, mb: 0.4, textTransform: "uppercase" }}>End</Typography>
                    <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: savedEnd ? UI.text : UI.textMuted, fontStyle: savedEnd ? "normal" : "italic", fontFamily: "'Inter', sans-serif" }}>
                      {fmtDisplay(selectedData?.recurringEndDate)}
                    </Typography>
                  </Box>
                </Stack>
              )}

              {/* Cron preview — shows as soon as both dates are filled */}
              {isAdmin && draft.startDate && draft.endDate && (
                <CronPreview startStr={draft.startDate} endStr={draft.endDate} />
              )}

              {/* Save button */}
              {isAdmin && (
                <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
                  <Tooltip
                    title={
                      !draft.startDate || !draft.endDate
                        ? "Enter both start and end dates to save"
                        : !isDateRangeValid
                        ? "Fix the date range — no valid monthly repeat falls within selected dates"
                        : !isDirty
                        ? "No changes to save"
                        : ""
                    }
                    placement="top"
                  >
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!canSave || saving}
                        onClick={handleSave}
                        startIcon={<FiSave size={12} />}
                        sx={{
                          fontSize: "11.5px", fontWeight: 700, textTransform: "none",
                          borderRadius: "8px", px: 2, py: 0.7,
                          background: canSave ? `linear-gradient(135deg, ${UI.accent}, ${UI.accentDark})` : undefined,
                          "&:hover": { background: `linear-gradient(135deg, ${UI.accentDark}, #085E56)` },
                          boxShadow: canSave ? "0 2px 10px rgba(13,148,136,0.28)" : "none",
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {saving ? "Saving…" : "Save"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Disable confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} PaperProps={{ sx: { borderRadius: "14px", p: 0.5, minWidth: 300 } }}>
        <DialogTitle sx={{ fontSize: "14px", fontWeight: 700, color: UI.text, fontFamily: "'Inter', sans-serif", pb: 0.5 }}>
          Disable Recurring?
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: "13px", color: UI.textMuted, fontFamily: "'Inter', sans-serif" }}>
            This will remove the recurring schedule and clear the start and end dates. Are you sure?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
          <Button size="small" onClick={() => setConfirmOpen(false)}
            sx={{ fontSize: "12px", fontWeight: 600, textTransform: "none", color: UI.textMuted, borderRadius: "8px", fontFamily: "'Inter', sans-serif" }}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleConfirmDisable}
            sx={{ fontSize: "12px", fontWeight: 700, textTransform: "none", borderRadius: "8px", background: "#EF4444", "&:hover": { background: "#DC2626" }, boxShadow: "none", fontFamily: "'Inter', sans-serif" }}>
            Yes, Disable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecurringCard;
